import { lazy } from 'react';

import { useAppConfig } from '#/config/AppConfigContext';
import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerContext } from '#/horizon/server-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18N_LOCALES } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { listingApiIsomorphicFetchEvent } from '#/modules/listing/events/api/listingApiFetchEvent';
import { getListingPublishedDetails } from '#/modules/listing/helpers/getListingPublishedDetails';
import { isListingExpired } from '#/modules/listing/helpers/isListingExpired';
import type { viewEventReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { ClientOnlyLazySearchEventsRefinements } from '#/modules/search/events/components/Refinements/ClientOnlyLazySearchEventsRefinements';
import { userDashboardApiIsomorphicFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { LazyNotFoundPage } from '#/pages/NotFound/LazyNotFoundPage';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { imageUrlForOgMeta } from '#/utils/filestack';

import { ViewEventPage } from './ViewEventPage';

const LazyExpiredEventPage = lazy(() =>
  import('#/modules/listing/events/pages/expired/ExpiredEventPage').then(
    (m) => ({ default: m.ExpiredEventPage }),
  ),
);

const LazyUnpublishedEventPage = lazy(() =>
  import('#/modules/listing/events/pages/unpublished/UnpublishedEventPage').then(
    (m) => ({ default: m.UnpublishedEventPage }),
  ),
);

export const handle = {
  defaultSearchType: 'EVENT',
  showSearchRefinements: true,
  showDesktopSearch: true,
  searchRefinements: <ClientOnlyLazySearchEventsRefinements />,
} satisfies LayoutHandleProps;

export async function loader({
  request,
  context,
  params: { eventIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewEventReferenceRoute>) {
  const { serverContext } = getServerContext(context);

  const [eventId] = eventIdDashSlug.split('-');

  try {
    const [event, listingExtras] = await Promise.all([
      listingApiIsomorphicFetchEvent({ request, context })(eventId),
      serverContext.hasNoCacheCookie
        ? userDashboardApiIsomorphicFetchListingExtras({
            request,
            context,
          })([eventId])
        : null,
    ]);

    switch (event.type) {
      case 'EVENT': {
        const expired = isListingExpired(event.expires);

        return {
          type: expired ? 'EXPIRED_EVENT' : event.type,
          event,
          publishedDetails: getListingPublishedDetails({
            publishedDate: event.firstPublished,
            status: expired
              ? ListingViewStatus.EXPIRED
              : ListingViewStatus.PUBLISHED,
            context,
          }),
          locale: serverContext.locale,
          handle: {
            hideFooterDivider: 'hide-footer-divider-if-logged-out',
            searchFromPageType: 'LISTING',
            urlByLocale: event.url,
          } satisfies LayoutHandleProps,
          saved: Boolean(listingExtras?.[eventId]?.saved),
        } as const;
      }
      case 'UNPUBLISHED_EVENT':
        return idealistNotFound({
          type: event.type,
          unpublishedEvent: event,
        } as const);
    }
  } catch {
    return idealistNotFound(null);
  }
}

export default function ViewEventHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();

  switch (loaderData?.type) {
    case undefined:
      return <LazyNotFoundPage />;
    case 'EVENT':
      return (
        <RouteModule
          title={`${loaderData.event.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['EVENT']}`}
          ogImage={imageUrlForOgMeta({
            data: loaderData.event.org?.logo,
            appConfig,
          })}
          description={loaderData.event.descriptionTruncated.slice(0, 200)}
          schemaMarkups={[
            loaderData.event.schemaEventMarkup?.[loaderData.locale],
            loaderData.event.schemaBreadcrumbListMarkup?.[loaderData.locale],
          ]}
          linkRels={{
            translations: I18N_LOCALES.map((locale) => {
              const url = loaderData.event.url[locale];
              if (url) return { url, locale };
            }).filter(Boolean),
            canonical: loaderData.event.url[loaderData.event.locale],
          }}
        >
          <ViewEventPage
            event={loaderData.event}
            publishedDetails={loaderData.publishedDetails}
            initiallySaved={loaderData.saved}
          />
        </RouteModule>
      );

    case 'EXPIRED_EVENT':
      return (
        <RouteModule title={loaderData.event.name}>
          <LazyExpiredEventPage
            event={loaderData.event}
            publishedDetails={loaderData.publishedDetails}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_EVENT':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <LazyUnpublishedEventPage
            unpublishedEvent={loaderData.unpublishedEvent}
          />
        </RouteModule>
      );
  }
}
